import defaultIcon from '@/pages/intelligence/assets/svg/dataSource/defaultSmall.svg';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';

export const getUrlBySelfs = (dataSource: string) => {
  switch (dataSource) {
    case 'Sparkers(GSD)':
    case 'sparkers':
    case 'Other Vendor':
    case 'gsd':
      return '/intelligence/topCharts/pcConsoleGames';
    default:
      return '';
  }
};

export type TDataSourceFilterOption = {
  text: string;
  value: string;
  img: string;
};

export type TDataSourceRecord = {
  data_source?: string;
  icon?: string;
};

const resolveDataSourceIcon = (item?: TDataSourceRecord) => {
  const icon = item?.icon?.trim();
  if (icon && /^https?:\/\//i.test(icon)) {
    return icon;
  }
  const dataSource = item?.data_source?.trim();
  if (dataSource && /^https?:\/\//i.test(dataSource)) {
    return dataSource;
  }
  return getDataSourceIcon(dataSource || '') || defaultIcon;
};

export const getRecordIconSrc = (record?: TDataSourceRecord) => {
  return resolveDataSourceIcon(record);
};

/** URL pageid 与动态下拉 option.value 的别名映射（兼容历史跳转链接） */
const LEGACY_DATA_SOURCE_ALIASES: Array<{ value: string; aliases: string[] }> = [
  { value: 'data.ai', aliases: ['data.ai', 'appannie', 'appnnie'] },
  { value: 'AppStore', aliases: ['appstore', 'AppStore', 'App Store'] },
  { value: 'DataBrain', aliases: ['databarin', 'ogdb', 'databrain', 'Newzoo', 'newzoo'] },
  { value: 'Epic', aliases: ['epic'] },
  { value: 'GooglePlay', aliases: ['googleplay', 'GooglePlay', 'Google Play'] },
  { value: 'MScience', aliases: ['mscience'] },
  { value: 'NPD', aliases: ['npd'] },
  { value: 'Sensor Tower', aliases: ['sensortower', 'Sensor Tower'] },
  { value: 'Steam', aliases: ['steam', 'steamcharts'] },
  { value: 'Stream Hatchet', aliases: ['hatchet', 'Stream Hatchet'] },
  { value: 'Twitch', aliases: ['twitch'] },
  { value: 'Xbox', aliases: ['xbox'] },
  { value: 'Nintendo', aliases: ['nintendo'] },
  { value: 'PlayStation', aliases: ['playstation', 'Playstation', 'Play Station'] },
  // Famitsu is temporarily unavailable throughout Intelligence pages.
  // { value: 'famitsu', aliases: ['famitsu'] },
  { value: 'vginsights', aliases: ['vginsights'] },
  { value: 'Sparkers(GSD)', aliases: ['gsd', 'sparkers'] },
  { value: 'Other Vendor', aliases: ['Other Vendor', 'ampere'] },
  { value: 'Gamalytic', aliases: ['gamalytic', 'Gamalytic'] },
  { value: 'Alinea', aliases: ['alinea', 'Alinea', 'Alinea Analytics'] },
];

const normalizeDataSourceKey = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, '');

/** 将 URL 中的 pageid 归一化为接口/筛选项使用的数据源名称 */
export const resolveLegacyPageId = (pageId: string): string => {
  const decoded = decodeURIComponent(pageId || '').trim();
  if (!decoded) {
    return '';
  }
  const normalized = normalizeDataSourceKey(decoded);
  const matched = LEGACY_DATA_SOURCE_ALIASES.find(
    item =>
      normalizeDataSourceKey(item.value) === normalized ||
      item.aliases.some(alias => normalizeDataSourceKey(alias) === normalized),
  );
  return matched?.value || decoded;
};

/** 将 URL pageid 列表映射为动态下拉可用的 option.value */
export const resolveDataSourceFilterValues = (
  pageIds: string[],
  options: TDataSourceFilterOption[] = [],
): string[] => {
  const legacyResolved = pageIds.map(resolveLegacyPageId).filter(Boolean);
  const resolved = !options.length
    ? legacyResolved
    : legacyResolved.map(id => {
      const normalizedId = normalizeDataSourceKey(id);
      const matched = options.find(
        option =>
          normalizeDataSourceKey(option.value) === normalizedId ||
          normalizeDataSourceKey(option.text) === normalizedId,
      );
      return matched?.value || id;
    });

  const isAlinea = legacyResolved.some(
    id => normalizeDataSourceKey(id) === normalizeDataSourceKey('Alinea Analytics'),
  );

  return isAlinea
    ? Array.from(new Set([...resolved, 'Alinea Analytics', 'Alinea']))
    : resolved;
};

type TTabListResponseItem = {
  language?: string;
  list?: TDataSourceRecord[];
};

/** 从 metrics/sources 列表接口响应中提取数据源记录（合并多语言） */
export const extractDataSourceRecordsFromTabResponse = (
  data?: TTabListResponseItem[],
): TDataSourceRecord[] => {
  const records: TDataSourceRecord[] = [];
  if (!Array.isArray(data)) {
    return records;
  }
  data.forEach(item => {
    item.list?.forEach(row => {
      if (row?.data_source?.trim()) {
        records.push(row);
      }
    });
  });
  return records;
};

/** 从未筛选的列表接口响应构建完整数据源下拉选项 */
export const buildDataSourceFilterOptionsFromTabResponse = (
  data?: TTabListResponseItem[],
): TDataSourceFilterOption[] => {
  return buildDataSourceFilterOptions(extractDataSourceRecordsFromTabResponse(data));
};

export const buildDataSourceFilterOptions = (
  list?: TDataSourceRecord[],
): TDataSourceFilterOption[] => {
  if (!Array.isArray(list)) {
    return [];
  }
  const optionMap = new Map<string, TDataSourceFilterOption>();
  list.forEach(item => {
    const dataSource = item?.data_source?.trim();
    if (!dataSource || optionMap.has(dataSource)) {
      return;
    }
    optionMap.set(dataSource, {
      text: dataSource,
      value: dataSource,
      img: resolveDataSourceIcon(item),
    });
  });
  return Array.from(optionMap.values());
};
